import { API, postJSON } from "../../api.js";
import { optionsCache } from "../../app/shell.js";
import { $, esc, fmtMoney, opt } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { moneySliderMax, sliderField, toast } from "../../ui/modals.js";
import { renderBriefingInto } from "./briefing.js";
import { openDealPostmortemModal } from "./deals.js";

const INCENTIVE_EXPLAIN = {
  "Tax Holiday": (v, m) => `${(v * 100).toFixed(0)}% tax rate reduction for ${m} months`,
  "Cash Subsidy Grant": (v) => `${fmtMoney(v)} direct cash grant`,
  "Infrastructure Investment Grant": (v, m) => `+${(v * 100).toFixed(2)}%/mo growth for ${m} months`,
  "Trade Agreement": (v, m) => `+${(v * 100).toFixed(2)}%/mo growth for ${m} months`,
};

async function renderGovernment(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">Government Relations — Month ${snap.month} ${helpIcon("government")}</h2>
    <p style="font-size:12.5px; color:var(--text-mid); margin-bottom:20px;">Visit a country's leadership to negotiate real investment incentives — tax holidays, cash subsidies, infrastructure grants, or trade agreements. Finalizing registers that country on your Expansion Tracker.</p>
    <div class="commands">
      <button class="primary" id="btn-visit-country">Visit a Country</button>
      <button id="btn-investment-outlook">Investment Outlook</button>
    </div>
    <div id="gov-deals">Loading…</div>
    <div id="gov-outlook-result" style="margin-top:24px;"></div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:32px 0 6px;">Country Expansion</h3>
    <p style="font-size:12.5px; color:var(--text-mid); margin-bottom:14px;">A different decision from the incentive negotiation above — commit capital to actually entering a market, via partnership, greenfield build, acquisition, joint venture, or export. Real cost/speed/control trade-offs, and a genuine multi-month establishment period.</p>
    <div class="commands">
      <button class="primary" id="btn-start-country-expansion">Explore a Country</button>
    </div>
    <div id="country-expansion-pipeline">Loading…</div>

    <div id="gov-briefing" style="margin-top:32px;"></div>
  `;
  $("btn-visit-country").addEventListener("click", window.openVisitCountryModal);
  $("btn-investment-outlook").addEventListener("click", openInvestmentOutlookModal);
  $("btn-start-country-expansion").addEventListener("click", openCountryExpansionModal);
  renderCountryExpansionPipeline();
  renderBriefingInto("gov-briefing", "/government/report", [
    ["Countries Engaged", (s) => s.countries_engaged.length], ["Open Negotiations", (s) => s.open_count],
    ["Finalized", (s) => s.finalized_count], ["Total Committed", (s) => fmtMoney(s.total_investment_committed)],
  ]);
  const deals = await (await fetch(`${API}/government/deals`)).json();
  const list = $("gov-deals");
  if (!deals.length) {
    list.innerHTML = `<p style="color:var(--text-mid)">No government meetings yet. Visit a country to start a negotiation.</p>`;
    return;
  }

  // Comparison table first — "which country is giving the best incentive" at a glance
  const openDeals = deals.filter((d) => d.status === "open");
  if (openDeals.length > 1) {
    list.innerHTML += `<h3 style="font-family:var(--font-display); font-size:16px; margin-bottom:10px;">Comparing Open Offers</h3>`;
  }
  const comparisonHtml = openDeals.length > 1 ? `
    <table class="ledger" style="margin-bottom:24px;">
      <tr><th>Country</th><th>Incentive</th><th>Value</th><th>Investment Required</th><th>Probability</th></tr>
      ${openDeals.map((d) => `
        <tr>
          <td class="label">${esc(d.country_name)}</td>
          <td class="label">${esc(d.incentive_type)}</td>
          <td class="mono">${INCENTIVE_EXPLAIN[d.incentive_type](d.incentive_value, d.incentive_months)}</td>
          <td class="mono">${fmtMoney(d.investment_required)}</td>
          <td class="mono">${Math.round(d.probability * 100)}%</td>
        </tr>
      `).join("")}
    </table>
  ` : "";

  const cardsHtml = deals.map((d) => `
    <div class="deal-card">
      <div class="deal-card-top">
        <div class="deal-card-name">${esc(d.country_name)}</div>
        <span class="status-pill ${d.status}">${esc(d.stage)}</span>
      </div>
      <div class="deal-meta">${esc(d.incentive_type)} · ${INCENTIVE_EXPLAIN[d.incentive_type](d.incentive_value, d.incentive_months)} · Requires ${fmtMoney(d.investment_required)} · Probability ${Math.round(d.probability * 100)}% · Met M${d.created_month}${d.negotiation_count ? ` · Renegotiated ${d.negotiation_count}x` : ""}</div>
      <div style="font-size:11px; color:var(--text-low); margin-bottom:8px;"><strong style="color:var(--text-mid);">In attendance:</strong> ${d.attendees.map(esc).join(", ")}</div>
      <div style="font-size:13px; color:var(--text-mid); margin-bottom:10px; line-height:1.5;">${esc(d.discussion)}</div>
      ${d.status === "open" ? `
        <div class="deal-actions">
          <button class="primary" data-finalize="${d.id}">Finalize Agreement</button>
          ${(d.negotiation_count || 0) < 2 ? `
            <button data-negotiate-gov="${d.id}" data-tactic="aggressive" title="Bigger potential upside (+10pp probability, +8% incentive, -5% investment required) — but a real 15% chance the delegation digs in and probability drops instead. Costs the full advisory fee either way.">Push Hard</button>
            <button data-negotiate-gov="${d.id}" data-tactic="collaborative" title="Smaller guaranteed gain (+5pp probability only) — zero risk of making things worse. Half the advisory fee.">Build Relationship</button>
          ` : ""}
          <button data-reject-gov="${d.id}">Decline</button>
        </div>
      ` : `<button data-postmortem-gov="${d.id}" style="margin-top:4px;">Why did this happen?</button>`}
    </div>
  `).join("");

  list.innerHTML += comparisonHtml + cardsHtml;

  list.querySelectorAll("[data-finalize]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/government/finalize", { deal_id: parseInt(btn.dataset.finalize) });
      await window.refreshAll();
      toast(res.message);
    });
  });
  list.querySelectorAll("[data-negotiate-gov]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/government/negotiate", { deal_id: parseInt(btn.dataset.negotiateGov), tactic: btn.dataset.tactic });
      await window.refreshAll();
      toast(res.message);
    });
  });
  list.querySelectorAll("[data-postmortem-gov]").forEach((btn) => {
    btn.addEventListener("click", () => openDealPostmortemModal("/government", parseInt(btn.dataset.postmortemGov)));
  });
  list.querySelectorAll("[data-reject-gov]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      await postJSON("/government/reject", { deal_id: parseInt(btn.dataset.rejectGov) });
      await window.refreshAll();
      toast("Offer rejected.");
    });
  });
}

function openExpandMarketModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>Expand Market</h3>
      <p style="font-size:12.5px; color:var(--text-mid); margin:-8px 0 14px;">Boosts growth for ~9 months and raises overhead. Picking a new country also adds real FX exposure.</p>
      <div class="field"><label>Country</label><select id="modal-country">${opt(optionsCache.countries)}</select></div>
      ${sliderField("modal-amount", "Budget", { min: 0, max: moneySliderMax(200000), value: 200000 })}
      <div class="sx-forecast" id="modal-ghost"></div>
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">Commit Budget</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  // Ghost Futures: the company with and without this expansion
  const ghost = window.uiGhostPanel(backdrop.querySelector("#modal-ghost"));
  const ghostRun = () => {
    const amount = parseFloat(backdrop.querySelector("#modal-amount").value) || 0;
    ghost.run(amount > 0 ? { type: "expand_market", params: { amount, country_name: backdrop.querySelector("#modal-country").value } } : null);
  };
  backdrop.addEventListener("input", ghostRun);
  backdrop.addEventListener("change", ghostRun);
  ghostRun();
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const country_name = backdrop.querySelector("#modal-country").value;
    const amount = parseFloat(backdrop.querySelector("#modal-amount").value) || 200000;
    backdrop.remove();
    const res = await postJSON("/commands/expand-market", { amount, country_name });
    await window.refreshAll();
    toast(res.message);
  });
}

function openVisitCountryModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>Visit a Country</h3>
      <div class="field"><label>Country</label><select id="modal-country">${opt(optionsCache.countries)}</select></div>
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">Request Meeting</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const country_name = backdrop.querySelector("#modal-country").value;
    backdrop.remove();
    const res = await postJSON("/government/visit", { company_name: country_name });
    await window.refreshAll();
    toast(res.message);
  });
}

function openInvestmentOutlookModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>Investment Outlook</h3>
      <p style="font-size:12.5px; color:var(--text-mid); margin:-8px 0 14px;">Pick a country and a commitment level to see a detailed, planning-only estimate of what incentives that level of investment could realistically unlock — before spending a real Visit on it.</p>
      <div class="field"><label>Country</label><select id="modal-country">${opt(optionsCache.countries)}</select></div>
      ${sliderField("modal-amount", "Investment to consider", { min: 0, max: Math.max(20000000, moneySliderMax(1000000)), value: 1000000 })}
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">Get Outlook</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const country_name = backdrop.querySelector("#modal-country").value;
    const amount = parseFloat(backdrop.querySelector("#modal-amount").value) || 1000000;
    backdrop.remove();
    const resultEl = $("gov-outlook-result");
    if (!resultEl) return;
    resultEl.innerHTML = `<p style="color:var(--text-mid); font-size:12.5px;">Preparing outlook…</p>`;
    try {
      const res = await fetch(`${API}/government/outlook`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country_name, amount }),
      });
      const data = await res.json();
      if (!res.ok) {
        resultEl.innerHTML = `<p style="color:var(--oxblood); font-size:12.5px;">${esc(data.detail || "Could not generate outlook.")}</p>`;
        return;
      }
      resultEl.innerHTML = `
        <h3 style="font-family:var(--font-display); font-size:16px; margin-bottom:10px;">Investment Outlook — ${esc(country_name)}</h3>
        <div class="report-narrative report" style="max-height:420px; overflow-y:auto;">${esc(data.markdown)}</div>
      `;
      resultEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch (e) {
      resultEl.innerHTML = `<p style="color:var(--oxblood); font-size:12.5px;">Could not reach the server for an outlook.</p>`;
    }
  });
}

// --- Country Expansion (Intelligence -> Entry Mode -> Establishing -> Established) ---

const COUNTRY_ENTRY_MODE_LABEL = {
  partnership: "Local Partnership", greenfield: "Greenfield Investment", acquisition: "Acquisition",
  joint_venture: "Joint Venture", export: "Export / Remote Service",
};

const COUNTRY_EXPANSION_STAGE_LABEL = { Establishing: "Establishing", Established: "Established", Withdrawn: "Withdrawn" };

/** country: optional market to open on (the World screen passes the one you tapped). */
const uiMoneyShort = (v) => {
  const n = Math.abs(v || 0);
  return n >= 1e12 ? `$${(v / 1e12).toFixed(1)}T` : n >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : n >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : fmtMoney(v);
};

function openCountryExpansionModal(country) {
  // A market-entry study, the way a real company decides (backend/country_brief.py):
  // the market, the risks, the rules, every entry mode side by side, the steps. Then commit.
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal ce-modal" role="dialog" aria-label="Explore a country">
      <button type="button" class="modal-x" id="ce-x" aria-label="Close">✕</button>
      <div class="sx-eyebrow">Market-entry study</div>
      <h3>Explore a country</h3>
      <div class="field"><label>Country</label><select id="modal-country">${opt(optionsCache.countries)}</select></div>
      <div id="ce-study"><p class="ce-loading">Commissioning the study…</p></div>
      <h4 class="ce-h">How much you'll commit</h4>
      ${sliderField("modal-investment", "Investment", { min: 0, max: Math.max(5000000, moneySliderMax(500000)), value: 500000 })}
      <div id="ce-cost" class="ce-cost"></div>
      <div id="gov-incentive" class="gx-incentive"></div>
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok" disabled>Commit and meet the government</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  const countrySel = backdrop.querySelector("#modal-country");
  if (country && [...countrySel.options].some((o) => o.value === country)) countrySel.value = country;
  const studyEl = backdrop.querySelector("#ce-study");
  const incEl = backdrop.querySelector("#gov-incentive");
  const invEl = backdrop.querySelector("#modal-investment");
  const costEl = backdrop.querySelector("#ce-cost");
  const okBtn = backdrop.querySelector("#modal-ok");
  let study = null, mode = null, seq = 0;
  const close = () => backdrop.remove();
  backdrop.querySelector("#ce-x").addEventListener("click", close);
  backdrop.querySelector("#modal-cancel").addEventListener("click", close);

  const paintCost = () => {
    const m = study && study.modes.find((x) => x.key === mode);
    if (!m) { costEl.innerHTML = ""; return; }
    const inv = parseFloat(invEl.value) || 0;
    const setup = inv * m.setup_share_pct / 100;
    costEl.innerHTML = `<div><em>Setup, legal, licences</em><b>${fmtMoney(setup)}</b><span>an expense this month</span></div>
      <div><em>Assets you build</em><b>${fmtMoney(inv - setup)}</b><span>stays on your balance sheet</span></div>
      <div><em>Open for business in</em><b>about ${m.months} month${m.months === 1 ? "" : "s"}</b><span>${study.rules.extra_months ? `${study.rules.extra_months} of them licences and paperwork` : "no special licences"}</span></div>`;
    const terms = study.intel.government;
    if (!terms) { incEl.innerHTML = ""; return; }
    const need = terms.investment_required, ok = inv >= need;
    incEl.innerHTML = `<div class="gx-inc-head">🏛 ${esc(terms.official)} ${terms.open ? "has an open offer" : "will meet you"}: <b>${esc(terms.incentive_type)}</b></div>
      <div class="gx-inc-bar"><i style="width:${Math.min(100, (100 * inv) / need).toFixed(1)}%" class="${ok ? "ok" : ""}"></i></div>
      <div class="gx-inc-line ${ok ? "ok" : ""}">${ok ? `✓ Your investment matches their ${fmtMoney(need)} level: the incentive is granted when you commit.`
        : `Invest ${fmtMoney(need - inv)} more (${fmtMoney(need)} in all) to unlock it now. Below that, their offer stays open in Government Relations.`}</div>`;
  };
  invEl.addEventListener("input", paintCost);

  const pickMode = (key) => {
    mode = key;
    studyEl.querySelectorAll("[data-ce-mode]").forEach((b) => b.classList.toggle("on", b.dataset.ceMode === key));
    paintCost();
  };

  const load = async () => {
    const mine = ++seq;
    okBtn.disabled = true;
    studyEl.innerHTML = `<p class="ce-loading">Commissioning the study on ${esc(countrySel.value)}…</p>`;
    let d;
    try {
      const r = await fetch(`${API}/country-expansion/study/${encodeURIComponent(countrySel.value)}`);
      if (!r.ok) throw new Error((await r.json()).detail || "No study for that country.");
      d = await r.json();
    } catch (e) {
      if (mine === seq) studyEl.innerHTML = `<p class="iv-warn">${esc(e.message)}</p>`;
      return;
    }
    if (mine !== seq) return;
    study = d;
    const i = d.intel, vTone = { Go: "good", "Go carefully": "warn", "Not now": "bad" }[d.verdict];
    studyEl.innerHTML = `
      <div class="ce-verdict ${vTone}"><b>${esc(d.verdict)}</b><span>${esc(d.country)} · ${esc(d.continent || "")} · ${esc(d.currency || "")} · ${esc(i.economic_cycle_phase)}</span></div>
      ${i.already_present ? `<p class="ce-note good">You already operate here.</p>` : ""}
      ${i.existing_expansion ? `<p class="ce-note">An expansion into ${esc(d.country)} is already ${esc(i.existing_expansion.status)}.</p>` : ""}
      <div class="ce-grid">
        <div><em>Economy</em><b>$${d.market.gdp_bn >= 1000 ? `${(d.market.gdp_bn / 1000).toFixed(1)}T` : `${d.market.gdp_bn >= 10 ? Math.round(d.market.gdp_bn) : d.market.gdp_bn.toFixed(1)}B`}</b><span>GDP growth ${d.market.growth_pct}%</span></div>
        <div><em>Your market there</em><b>${uiMoneyShort(d.market.your_market)}/yr</b><span>spent on what you sell</span></div>
        <div><em>Tax on profit</em><b>${d.costs.tax_pct}%</b><span>interest rates ${d.costs.interest_pct}%</span></div>
        <div><em>Hiring there costs</em><b>${d.costs.labor_vs_home_pct}%</b><span>of what it costs at home</span></div>
        ${d.economy ? `<div><em>People</em><b>${(d.economy.population / 1e6).toFixed(1)}M</b><span>$${Math.round(d.economy.gdp_per_capita).toLocaleString("en-US")} per person</span></div>
        <div><em>Foreign investment</em><b>${d.economy.fdi_inflow === null ? "—" : fmtMoney(d.economy.fdi_inflow)}</b><span>came in last year</span></div>
        <div><em>Investment climate</em><b>${esc(d.economy.climate)}</b><span>${esc(d.economy.income_group)}</span></div>` : ""}
      </div>
      ${d.economy ? `<p class="gov-file-src">Real figures: ${esc(d.economy.source)}${d.economy.year ? `, ${d.economy.year}` : ""}.</p>` : ""}
      <h4 class="ce-h">The risks</h4>
      <div class="ce-risks">${d.risks.map((r) => `<div class="ce-risk l${r.level}"><span>${esc(r.label)}</span>
        <i aria-hidden="true">${[0, 1, 2].map((k) => `<u class="${k < r.level ? "on" : ""}"></u>`).join("")}</i><b>${esc(r.word)}</b><p>${esc(r.why)}</p></div>`).join("")}</div>
      <h4 class="ce-h">How to enter <span>pick one: ★ is what the study recommends</span></h4>
      <div class="ce-modes">${d.modes.map((m) => `<button type="button" class="ce-mode" data-ce-mode="${esc(m.key)}">
        <b>${m.key === d.best_mode ? "★ " : ""}${esc(m.label)}</b>
        <span>${m.months} mo to open · ${esc(m.control)} control</span>
        <span>+${m.growth_pts} pts growth for ${m.boost_months} mo</span>
        <strong>≈ ${fmtMoney(m.extra_revenue)}</strong><em>extra revenue at today's size</em>
        <p>${esc(m.description)}</p>${m.warning ? `<p class="ce-warn">⚠ ${esc(m.warning)}</p>` : ""}</button>`).join("")}</div>
      <h4 class="ce-h">The steps</h4>
      <ol class="ce-steps">${d.steps.map((st) => `<li><b>${esc(st.step)}</b>${st.months ? `<em>${st.months} mo</em>` : ""}<span>${esc(st.what)}</span></li>`).join("")}</ol>`;
    studyEl.querySelectorAll("[data-ce-mode]").forEach((b) => b.addEventListener("click", () => pickMode(b.dataset.ceMode)));
    pickMode(d.best_mode);
    okBtn.disabled = !!i.already_present && !d.modes.length;
  };
  countrySel.addEventListener("change", load);
  load();

  okBtn.addEventListener("click", async () => {
    if (!mode) return;
    const country_name = countrySel.value;
    const investment = parseFloat(invEl.value) || 0;
    okBtn.disabled = true;
    okBtn.textContent = "Committing…";
    try {
      const res = await postJSON("/country-expansion/start", { country_name, entry_mode: mode, investment });
      close();
      await window.refreshAll();
      renderCountryExpansionPipeline();
      toast(res.message);
    } catch (e) {
      okBtn.disabled = false;
      okBtn.textContent = "Commit and meet the government";
      toast(e.message, true);
    }
  });
}

// Rivals that entered this country after the player began expanding there, and
// what that does to the entry boost. Fields come from /country-expansion/pipeline
// (rivals_present, contested_boost_cut_pct); absent/empty => nothing rendered.
function _rivalsInCountryHtml(e) {
  const list = Array.isArray(e.rivals_present) ? e.rivals_present : [];
  if (!list.length) return "";
  const names = list.map((r) => esc(r.name)).join(", ");
  const cut = e.contested_boost_cut_pct || 0;
  const effect = e.stage === "Established"
    ? "They are now contesting the market you entered."
    : (cut > 0 ? `If they are still there when you finish establishing, your entry boost shrinks by ${cut}%.` : "");
  return `<div class="rival-in-country" style="font-size:11.5px; color:var(--brass); margin-bottom:8px;">
    <strong>${names}</strong> ${list.length === 1 ? "has" : "have"} followed you into ${esc(e.country_name)}. ${effect}</div>`;
}

async function renderCountryExpansionPipeline() {
  const el = $("country-expansion-pipeline");
  if (!el) return;
  const expansions = await fetch(`${API}/country-expansion/pipeline`).then((x) => x.json());
  if (!expansions.length) {
    el.innerHTML = `<p style="color:var(--text-mid)">No country expansions in progress.</p>`;
    return;
  }
  el.innerHTML = expansions.map((e) => {
    const isOpen = e.status === "open";
    const progress = e.establishment_months_total > 0 ? Math.round(100 * e.establishment_months_elapsed / e.establishment_months_total) : 0;
    return `
      <div class="deal-card">
        <div class="deal-card-top">
          <div class="deal-card-name">${esc(e.country_name)}</div>
          <span class="status-pill ${isOpen ? "open" : (e.status === "established" ? "signed" : "rejected")}">${esc(COUNTRY_EXPANSION_STAGE_LABEL[e.stage] || e.stage)}</span>
        </div>
        <div class="deal-meta">
          ${esc(COUNTRY_ENTRY_MODE_LABEL[e.entry_mode] || e.entry_mode)} · ${fmtMoney(e.investment)} committed
          ${e.local_partner_name ? ` · Local partner: ${esc(e.local_partner_name)}` : ""}
        </div>
        ${_rivalsInCountryHtml(e)}
        ${e.stage === "Establishing" ? `
          <div style="position:relative; height:8px; border-radius:5px; background:var(--line); margin:8px 0;">
            <div style="position:absolute; left:0; top:0; height:100%; width:${progress}%; border-radius:5px; background:var(--brass);"></div>
          </div>
          <div style="font-size:11px; color:var(--text-low); margin-bottom:8px;">Establishing — month ${e.establishment_months_elapsed} of ${e.establishment_months_total}.</div>
        ` : ""}
        ${isOpen ? `<div class="deal-actions"><button data-withdraw-country="${e.id}">Withdraw</button></div>` : ""}
      </div>
    `;
  }).join("");
  el.querySelectorAll("[data-withdraw-country]").forEach((btn) => btn.addEventListener("click", async () => {
    const res = await postJSON("/country-expansion/withdraw", { expansion_id: parseInt(btn.dataset.withdrawCountry) });
    renderCountryExpansionPipeline();
    toast(res.message);
  }));
}

function openSellStakeModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>Sell a Stake</h3>
      <p style="font-size:12.5px; color:var(--text-mid); margin:-8px 0 14px;">Pick a % of your own founder ownership to shop to buyers. You'll see several real offers — different price, different strings — and choose one, or walk away.</p>
      ${sliderField("modal-pct", "Share of the company to sell", { min: 0.5, max: 60, step: 0.5, value: 10, unit: "pct" })}
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">Get Offers</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const pct = parseFloat(backdrop.querySelector("#modal-pct").value) || 10;
    backdrop.remove();
    const resultEl = $("stake-sale-result");
    if (!resultEl) return;
    resultEl.innerHTML = `<p style="color:var(--text-mid); font-size:12.5px;">Shopping the stake to potential buyers…</p>`;
    try {
      const res = await fetch(`${API}/investor-relations/shop-stake`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pct }),
      });
      const data = await res.json();
      if (!res.ok) {
        resultEl.innerHTML = `<p style="color:var(--oxblood); font-size:12.5px;">${esc(data.detail || "Could not get offers.")}</p>`;
        return;
      }
      renderStakeOffers(resultEl, data);
      resultEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch (e) {
      resultEl.innerHTML = `<p style="color:var(--oxblood); font-size:12.5px;">Could not reach the server for offers.</p>`;
    }
  });
}

function renderStakeOffers(el, data) {
  el.innerHTML = `
    <h3 style="font-family:var(--font-display); font-size:16px; margin-bottom:6px;">Offers for ${data.pct}% of the Company</h3>
    <p style="font-size:12px; color:var(--text-mid); margin-bottom:14px;">Current estimated valuation: ${fmtMoney(data.current_valuation)}. Ranked by price — but price isn't the only thing that matters.</p>
    ${data.offers.map((o) => `
      <div class="alert-card info" style="align-items:flex-start; margin-bottom:10px;">
        <div style="flex:1;">
          <div class="alert-category">${esc(o.archetype)}</div>
          <div class="alert-message">${esc(o.buyer_name)} — ${fmtMoney(o.price)} <span style="color:var(--text-low); font-weight:400;">(implies a ${fmtMoney(o.implied_company_valuation)} valuation)</span></div>
          <div style="color:var(--text-mid); font-size:12px; margin-top:4px;">${esc(o.pitch)}</div>
          <div style="color:var(--brass); font-size:12px; margin-top:5px;">
            ${o.board_seat_requested ? "Requests a board seat" : "No board seat requested"} ·
            ~${o.close_months} month${o.close_months === 1 ? "" : "s"} to close ·
            +${o.sentiment_boost} investor sentiment
          </div>
        </div>
        <button data-sell-to="${esc(o.buyer_name)}" data-pct="${data.pct}" style="align-self:center;">Sell to This Buyer</button>
      </div>
    `).join("")}
  `;
  el.querySelectorAll("[data-sell-to]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      if (!confirm(`Sell ${btn.dataset.pct}% of the company to ${btn.dataset.sellTo}? This is permanent.`)) return;
      try {
        const res = await postJSON("/investor-relations/sell-stake", {
          pct: parseFloat(btn.dataset.pct), buyer_name: btn.dataset.sellTo,
        });
        await window.refreshAll();
        toast(res.message);
        el.innerHTML = "";
      } catch (e) {
        toast(e.message || "Could not complete the sale — the offers may be stale, try shopping the stake again.");
      }
    });
  });
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.openVisitCountryModal = openVisitCountryModal;
window.renderGovernment = renderGovernment;

export { openCountryExpansionModal, openExpandMarketModal, openInvestmentOutlookModal, openSellStakeModal, renderCountryExpansionPipeline };
